import React from "react";

const InvoiceReceipt = ({ invoice }) => {
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatCurrency = (amount, currency) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const handlePrint = () => window.print();
  const handleDownload = () => alert("Invoice downloaded as PDF");

  if (!invoice) return null;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">Invoice Receipt</h1>
            <p className="text-blue-100 mt-1">
              Thank you for choosing CBT Host
            </p>
          </div>
          <div className="text-right">
            <div className="text-xl font-semibold">CBTHost</div>
            <div className="text-blue-100">cbthost.com</div>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-700">
              Invoice Details
            </h2>
            <div className="mt-2 space-y-1 text-gray-600">
              <p>
                <span className="font-medium">Invoice ID:</span> {invoice.id}
              </p>
              <p>
                <span className="font-medium">Date:</span>{" "}
                {formatDate(invoice.created_at)}
              </p>
              <p>
                <span className="font-medium">Due Date:</span>{" "}
                {formatDate(invoice.expires_at)}
              </p>
            </div>
          </div>

          <div className="md:text-right">
            <h2 className="text-lg font-semibold text-gray-700">
              Payment Status
            </h2>
            <div
              className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium space-x-2 ${
                invoice.status === "paid"
                  ? "bg-green-100 text-green-800"
                  : invoice.status === "pending"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <i
                className={`fa ${
                  invoice.status === "paid"
                    ? "fa-check-circle"
                    : invoice.status === "pending"
                    ? "fa-hourglass-half"
                    : "fa-times-circle"
                }`}
              ></i>
              <span>
                {invoice.status.charAt(0).toUpperCase() +
                  invoice.status.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Product Details
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoice.items.map((item, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {item.plan}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(item.amount, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td
                  colSpan="3"
                  className="px-6 py-3 text-right text-sm font-medium text-gray-900"
                >
                  Subtotal
                </td>
                <td className="px-6 py-3 text-sm text-gray-900">
                  {formatCurrency(invoice.subtotal, invoice.currency)}
                </td>
              </tr>
              <tr>
                <td
                  colSpan="3"
                  className="px-6 py-3 text-right text-sm font-medium text-gray-900"
                >
                  Tax (0%)
                </td>
                <td className="px-6 py-3 text-sm text-gray-900">
                  {formatCurrency(0, invoice.currency)}
                </td>
              </tr>
              <tr>
                <td
                  colSpan="3"
                  className="px-6 py-3 text-right text-sm font-bold text-gray-900"
                >
                  Total
                </td>
                <td className="px-6 py-3 text-sm font-bold text-gray-900">
                  {formatCurrency(invoice.total, invoice.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Payment Information */}
      <div className="p-6 border-t border-gray-200">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Payment Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium text-gray-900">
              Payment Method
            </h3>
            <p className="mt-1 text-sm text-gray-500 capitalize">
              {invoice.payment_method}
            </p>
          </div>
          <div>
            <h3 className="text-md font-medium text-gray-900">
              Payment Reference
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {invoice.payment_reference || "N/A"}
            </p>
          </div>
        </div>

        {invoice.payment_method === "bank" && invoice.bank_details && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-md font-medium text-gray-900">
              Bank Transfer Details
            </h3>
            <div className="mt-2 space-y-1 text-sm text-gray-600">
              <p>
                <span className="font-medium">Bank Name:</span>{" "}
                {invoice.bank_details.bank_name}
              </p>
              <p>
                <span className="font-medium">Account Name:</span>{" "}
                {invoice.bank_details.account_name}
              </p>
              <p>
                <span className="font-medium">Account Number:</span>{" "}
                {invoice.bank_details.account_number}
              </p>
              <p>
                <span className="font-medium">Reference:</span>{" "}
                {invoice.bank_details.reference}
              </p>
            </div>
          </div>
        )}
      </div>


      {/* Footer */}
      <div className="bg-gray-50 p-6 border-t border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm text-gray-500">
            <p>If you have any questions about this invoice, please contact</p>
            <p>
              support@cbthost.com or call{" "}
              <i className="fa fa-phone text-gray-600"></i> +234 903-394-21996
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <i className="fa fa-print mr-2"></i> Print
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700"
            >
              <i className="fa fa-download mr-2"></i> Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceReceipt;
